import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './RequestQuotePage.css'; // Ensure this CSS file exists and is styled
import '../assets/css/general.css'; // Ensure this path is correct

const RequestQuotePage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    productInterest: '',
    quantity: '',
    message: '',
  });

  // State for form submission status (loading, error, success)
  const [formStatus, setFormStatus] = useState({
    loading: false,
    error: '',
    success: '',
  });

  const location = useLocation();

  // --- Web3Forms Configuration ---
  const WEB3FORMS_ACCESS_KEY = "8d98ec0a-f9a7-44bb-bbd2-975d7f440d32"; // Your provided key
  const WEB3FORMS_ACTION_URL = "https://api.web3forms.com/submit";
  const WEB3FORMS_FROM_NAME = "ABP Quote Request"; // Name for this specific form

  useEffect(() => {
    window.scrollTo(0, 0);
    if (location.state && location.state.productName) {
      setFormData(prevData => ({ ...prevData, productInterest: location.state.productName }));
    }
  }, [location]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ loading: true, error: '', success: '' });

    // Construct the data payload for Web3Forms
    const payload = {
      ...formData,
      access_key: WEB3FORMS_ACCESS_KEY,
      from_name: WEB3FORMS_FROM_NAME,
      botcheck: '', // Optional: Web3Forms uses this for simple bot protection
      subject: `New Quote Request from ${formData.name || 'N/A'}: ${formData.productInterest || 'General Inquiry'}`, // Customize email subject
    };

    try {
      const response = await fetch(WEB3FORMS_ACTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.success) {
        setFormStatus({ loading: false, error: '', success: result.message || 'Your quote request has been sent successfully. We will get back to you soon!' });
        setFormData({ // Reset form
          name: '',
          email: '',
          company: '',
          productInterest: location.state?.productName || '', // Re-fill if from product page
          quantity: '',
          message: '',
        });
      } else {
        console.error("Web3Forms API Error:", result);
        setFormStatus({ loading: false, error: result.message || 'Failed to send your request. Please try again.', success: '' });
      }
    } catch (error) {
      console.error("Form Submission Error:", error);
      setFormStatus({ loading: false, error: 'An unexpected error occurred. Please check your connection or try again later.', success: '' });
    }
  };

  return (
    <main id="main" className="request-quote-page">
      <section id="breadcrumbs" className="breadcrumbs">
        <div className="container">
          <ol>
            {/* Assuming FontAwesome for home icon, ensure it's included in your project */}
            <li><Link to="/"><i className="fas fa-home"></i> Home</Link></li>
            <li><Link to="/products">Products</Link></li>
            <li>Request a Quote</li>
          </ol>
        </div>
      </section>

      <div className="container page-container-custom">
        <div className="form-header">
          <h1>Request a Quote</h1>
          <div className="title-bar-custom"></div>
          <p>Please fill out the form below to request a quote for our products. We will get back to you as soon as possible.</p>
        </div>

        <form onSubmit={handleSubmit} className="quote-form">
          <div className="form-group">
            <label htmlFor="name">Full Name <span className="required">*</span></label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              disabled={formStatus.loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address <span className="required">*</span></label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={formStatus.loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="company">Company (Optional)</label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              disabled={formStatus.loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="productInterest">Product(s) of Interest <span className="required">*</span></label>
            <input
              type="text"
              id="productInterest"
              name="productInterest"
              placeholder="e.g., Standard Big Bags, PE Film"
              value={formData.productInterest}
              onChange={handleChange}
              required
              disabled={formStatus.loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="quantity">Estimated Quantity (Optional)</label>
            <input
              type="text"
              id="quantity"
              name="quantity"
              value={formData.quantity}
              onChange={handleChange}
              disabled={formStatus.loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="message">Specific Requirements / Message (Optional)</label>
            <textarea
              id="message"
              name="message"
              rows="5"
              value={formData.message}
              onChange={handleChange}
              disabled={formStatus.loading}
            ></textarea>
          </div>

          {/* Form feedback messages */}
          <div className="form-feedback mb-3">
            {formStatus.loading && <div className="loading form-status">Sending your request...</div>}
            {formStatus.error && <div className="error-message form-status">{formStatus.error}</div>}
            {formStatus.success && <div className="sent-message form-status">{formStatus.success}</div>}
          </div>

          <button type="submit" className="btn-primary-style submit-quote-btn" disabled={formStatus.loading}>
            {formStatus.loading ? 'Sending...' : 'Submit Request'}
          </button>
        </form>
      </div>
    </main>
  );
};

export default RequestQuotePage;